import { useEffect, useRef, useState } from 'react'
import confetti from 'canvas-confetti'
import { X, TwitterLogo, Copy, DownloadSimple, Confetti as ConfettiIcon } from '@phosphor-icons/react'
import { toast } from './Toast'
import { exportElementToPng } from '../lib/pdf'

// Milestones we celebrate — first hit only, per user, per milestone.
const MILESTONES = [4, 8, 16, 26, 52] as const

const MILESTONE_LABEL: Record<number, string> = {
  4: 'one month',
  8: 'two months',
  16: 'four months',
  26: 'six months',
  52: 'a full year',
}

const STORAGE_KEY = 'lifeodds-streak-celebrated'

interface Props {
  weeklyStreak: number
  userId?: string
}

/** Returns the highest milestone <= streak the user has NOT yet celebrated, or null. */
function nextUncelebratedMilestone(streak: number, celebrated: Set<number>): number | null {
  for (let i = MILESTONES.length - 1; i >= 0; i--) {
    const m = MILESTONES[i]
    if (streak >= m && !celebrated.has(m)) return m
  }
  return null
}

function loadCelebrated(userId?: string): Set<number> {
  if (!userId) return new Set()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const map = JSON.parse(raw) as Record<string, number[]>
    return new Set(map[userId] || [])
  } catch {
    return new Set()
  }
}

function persistCelebrated(userId: string, milestones: Set<number>): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const map = raw ? (JSON.parse(raw) as Record<string, number[]>) : {}
    map[userId] = Array.from(milestones).sort((a, b) => a - b)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

/**
 * Fires a confetti burst from the bottom-center of the viewport, with two side cannons.
 * Brand colors: primary orange + secondary blue + accent green.
 */
function fireConfetti(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const colors = ['#FF5722', '#3B82F6', '#22C55E', '#FACC15', '#1A1A1A']
  const defaults = { colors, ticks: 240, gravity: 0.9, scalar: 1.1, shapes: ['square', 'circle'] as any }
  confetti({ ...defaults, particleCount: 90, spread: 70, origin: { x: 0.5, y: 1 } })
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 40, spread: 55, angle: 60, origin: { x: 0, y: 1 } })
    confetti({ ...defaults, particleCount: 40, spread: 55, angle: 120, origin: { x: 1, y: 1 } })
  }, 220)
}

export default function StreakCelebration({ weeklyStreak, userId }: Props) {
  const [milestone, setMilestone] = useState<number | null>(null)
  const [open, setOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const firedRef = useRef(false)

  useEffect(() => {
    if (!userId || weeklyStreak <= 0 || firedRef.current) return
    const celebrated = loadCelebrated(userId)
    const m = nextUncelebratedMilestone(weeklyStreak, celebrated)
    if (m === null) return
    firedRef.current = true
    setMilestone(m)
    // Toast first — gives a visible "🎲 4-week streak!" hit even if the user dismisses the modal.
    toast({
      kind: 'success',
      title: `🎲 ${m}-week roll streak!`,
      message: `You've run LifeOdds ${MILESTONE_LABEL[m]} straight. Wild.`,
      duration: 6000,
    })
    setTimeout(() => {
      fireConfetti()
      setOpen(true)
      // Persist AFTER the modal opens — guarantees the user got a visual hit.
      // (Toast also signals the milestone independently, so worst-case is a duplicate toast.)
      const updated = loadCelebrated(userId)
      updated.add(m)
      persistCelebrated(userId, updated)
    }, 350)
  }, [weeklyStreak, userId])

  if (!milestone || !open) return null

  const shareText = `I've run LifeOdds ${milestone} weeks in a row 🎲 — making big decisions with Monte Carlo + Bayes instead of vibes. lifeodds.app`
  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText)
      toast({ kind: 'success', title: 'Copied to clipboard' })
    } catch {
      toast({ kind: 'warning', title: 'Copy failed — please copy manually.' })
    }
  }

  const downloadCard = async () => {
    if (!cardRef.current) return
    try {
      await exportElementToPng(cardRef.current, { filename: `lifeodds-${milestone}-week-streak.png` })
      toast({ kind: 'success', title: 'Saved!', message: 'Your streak card is in Downloads — drag it into any social post.' })
    } catch {
      toast({ kind: 'warning', title: 'Save failed' })
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-ink/60 backdrop-blur-sm p-4"
      data-testid="streak-celebration-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="streak-celebration-title"
    >
      <div className="anim-pop relative max-w-md w-full">
        <button
          onClick={() => setOpen(false)}
          data-testid="streak-celebration-close"
          aria-label="Dismiss"
          className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-surface border-2 border-edge shadow-brutal-sm grid place-items-center hover:rotate-90 transition-transform"
        >
          <X size={16} weight="bold" />
        </button>

        {/* Social card preview — what we render and what gets downloaded */}
        <div
          ref={cardRef}
          className="card-brutal p-7 relative overflow-hidden bg-gradient-to-br from-primary via-primary to-secondary text-white"
          data-testid="streak-celebration-card"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent opacity-30" />
          <div className="absolute -left-8 -bottom-12 w-36 h-36 rounded-full bg-white opacity-15" />
          <div className="relative space-y-3">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-2 border-white/40 text-xs font-bold uppercase tracking-widest">
              <ConfettiIcon size={12} weight="fill" /> Streak unlocked
            </div>
            <h2
              id="streak-celebration-title"
              className="font-heading text-5xl font-black tracking-tighter leading-none"
            >
              {milestone}-week
              <br />
              roll streak.
            </h2>
            <p className="text-white/90 font-medium">
              You've run LifeOdds {MILESTONE_LABEL[milestone]} straight.{' '}
              <span className="font-bold">That's compounding into wisdom.</span>
            </p>
            <div className="pt-3 mono text-xs text-white/70">LifeOdds · Probabilistic decisions for the rest of us</div>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-4 flex flex-wrap gap-2 justify-center" data-html2canvas-ignore="true">
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="streak-share-tweet"
            className="btn-primary inline-flex items-center gap-2"
          >
            <TwitterLogo size={16} weight="fill" /> Tweet it
          </a>
          <button onClick={copyText} data-testid="streak-share-copy" className="btn-secondary inline-flex items-center gap-2">
            <Copy size={16} weight="fill" /> Copy text
          </button>
          <button onClick={downloadCard} data-testid="streak-share-download" className="btn-secondary inline-flex items-center gap-2">
            <DownloadSimple size={16} weight="fill" /> Save card
          </button>
        </div>
      </div>
    </div>
  )
}
